import React, { useEffect, useState, useCallback } from "react";
import TopBar from "@/components/TopBar";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { Copy, ArrowDownToLine, ArrowUpFromLine, RotateCcw, Send, FileDown } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

export default function Wallet() {
  const { t } = useI18n();
  const { user, refreshUser } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [txs, setTxs] = useState([]);
  const [depForm, setDep] = useState({ amount: 100, network: "", tx_hash: "" });
  const [wForm, setW] = useState({ amount: 50, network: "", address: "" });
  const [transferForm, setTransfer] = useState({ recipient_email: "", amount: 10 });

  const reload = useCallback(async () => {
    const [w, tx] = await Promise.all([api.get("/wallet"), api.get("/wallet/transactions")]);
    setWallet(w.data);
    setTxs(tx.data);
    if (w.data?.networks?.length > 0) {
      setDep(d => !d.network ? ({ ...d, network: w.data.networks[0].code }) : d);
      setW(prev => !prev.network ? ({ ...prev, network: w.data.networks[0].code }) : prev);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const submitDeposit = async (e) => {
    e.preventDefault();
    try { await api.post("/wallet/deposit", { ...depForm, amount: Number(depForm.amount) });
      toast.success(t("toast_deposit_submitted"));
      setDep({ ...depForm, tx_hash: "" }); reload();
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
  };
  const submitWithdraw = async (e) => {
    e.preventDefault();
    try { await api.post("/wallet/withdraw", { ...wForm, amount: Number(wForm.amount) });
      toast.success(t("toast_withdrawal_requested")); setW({ ...wForm, address: "" }); await refreshUser(); reload();
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
  };
  const resetDemo = async () => {
    try { await api.post("/account/demo/reset"); toast.success(t("toast_demo_reset")); await refreshUser(); reload(); }
    catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
  };

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success(t("toast_copied")); };
  const currentAddr = wallet?.networks?.find((n) => n.code === depForm.network)?.address || "";
  const currentFee = wallet?.networks?.find((n) => n.code === depForm.network);

  const submitTransfer = async (e) => {
    e.preventDefault();
    try {
      await api.post("/wallet/transfer", { ...transferForm, amount: Number(transferForm.amount) });
      toast.success(t("toast_transfer_success")); setTransfer({ recipient_email: "", amount: 10 }); await refreshUser(); reload();
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
  };

  const downloadTaxReport = async () => {
    try {
      const year = new Date().getFullYear();
      const res = await api.get(`/wallet/tax-report?year=${year}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `iqtrade_tax_${year}.csv`; a.click();
      toast.success(t("toast_tax_report"));
    } catch (er) { toast.error(t("toast_report_failed")); }
  };

  return (
    <div className="min-h-screen bg-app text-white">
      <TopBar />
      <div className="max-w-6xl mx-auto px-6 py-8">
        <h1 className="text-3xl font-bold tracking-tight mb-6">{t("wallet")}</h1>

        {/* Balances */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-line mb-8">
          <div className="bg-panel p-5">
            <div className="text-[10px] uppercase tracking-widest text-dim">{t("real_account")}</div>
            <div className="font-mono text-3xl font-bold mt-2" data-testid="real-balance">${Number(user?.real_balance ?? 0).toFixed(2)}</div>
          </div>
          <div className="bg-panel p-5">
            <div className="text-[10px] uppercase tracking-widest text-dim flex justify-between">{t("demo_account")}
              <button data-testid="reset-demo" onClick={resetDemo} className="text-buy hover:underline flex items-center gap-1"><RotateCcw className="w-3 h-3"/>{t("reset_demo")}</button>
            </div>
            <div className="font-mono text-3xl font-bold mt-2" data-testid="demo-balance">${Number(user?.demo_balance ?? 0).toFixed(2)}</div>
          </div>
          <div className="bg-panel p-5">
            <div className="text-[10px] uppercase tracking-widest text-dim">Deposit Address</div>
            <div className="font-mono text-xs mt-2 break-all text-muted">{wallet?.deposit_address ?? "…"}</div>
          </div>
        </div>

        {/* Deposit / Withdraw / Transfer */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-line mb-8">
          {/* Deposit */}
          <form onSubmit={submitDeposit} className="bg-panel p-5 space-y-4">
            <div className="flex items-center gap-2"><ArrowDownToLine className="w-4 h-4 text-buy" /><h2 className="font-semibold uppercase text-xs tracking-widest">{t("deposit")}</h2></div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-dim">{t("network")}</label>
              <select data-testid="deposit-network" value={depForm.network} onChange={(e)=>setDep({...depForm, network:e.target.value})} className="w-full mt-1.5 bg-app border border-line px-3 py-2 rounded-sm outline-none">
                {wallet?.networks?.map((n)=>(<option key={n.code} value={n.code}>{n.label}</option>))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-dim">{t("address")}</label>
              {currentAddr && (
                <div className="flex justify-center my-4 bg-app p-4 rounded-sm border border-line">
                  <QRCodeSVG value={currentAddr} size={160} bgColor="#161b28" fgColor="#ffffff" />
                </div>
              )}
              <div className="flex items-center gap-2 mt-1.5 bg-app border border-line px-3 py-2 rounded-sm">
                <span data-testid="deposit-address" className="font-mono text-xs flex-1 break-all">{currentAddr}</span>
                <button type="button" onClick={()=>copy(currentAddr)} data-testid="copy-address" className="text-muted hover:text-white"><Copy className="w-4 h-4"/></button>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-dim">{t("amount")} (USD)</label>
              <input data-testid="deposit-amount" type="number" min="1" step="0.01" value={depForm.amount} onChange={(e)=>setDep({...depForm, amount:e.target.value})}
                className="w-full mt-1.5 bg-app border border-line px-3 py-2 rounded-sm outline-none font-mono" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-dim">{t("tx_hash")}</label>
              <input data-testid="deposit-txhash" required value={depForm.tx_hash} onChange={(e)=>setDep({...depForm, tx_hash:e.target.value})}
                placeholder="0xabc..." className="w-full mt-1.5 bg-app border border-line px-3 py-2 rounded-sm outline-none font-mono" />
            </div>
            <button data-testid="submit-deposit" className="w-full bg-buy text-black font-bold py-2.5 rounded-sm hover:bg-emerald-400 uppercase tracking-widest text-xs">{t("submit")}</button>
            {currentFee && currentFee.fee_percent > 0 && (
              <div className="text-[10px] text-dim text-center">Ücret: %{currentFee.fee_percent} + ${currentFee.fee_fixed}</div>
            )}
          </form>

          {/* Withdraw */}
          <form onSubmit={submitWithdraw} className="bg-panel p-5 space-y-4">
            <div className="flex items-center gap-2"><ArrowUpFromLine className="w-4 h-4 text-sell" /><h2 className="font-semibold uppercase text-xs tracking-widest">{t("withdraw")}</h2></div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-dim">{t("network")}</label>
              <select data-testid="withdraw-network" value={wForm.network} onChange={(e)=>setW({...wForm, network:e.target.value})} className="w-full mt-1.5 bg-app border border-line px-3 py-2 rounded-sm outline-none">
                {wallet?.networks?.map((n)=>(<option key={n.code} value={n.code}>{n.label}</option>))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-dim">{t("address")}</label>
              <input data-testid="withdraw-address" required value={wForm.address} onChange={(e)=>setW({...wForm, address:e.target.value})}
                placeholder="Cüzdan adresi" className="w-full mt-1.5 bg-app border border-line px-3 py-2 rounded-sm outline-none font-mono" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-dim">{t("amount")} (USD)</label>
              <input data-testid="withdraw-amount" type="number" min="1" step="0.01" value={wForm.amount} onChange={(e)=>setW({...wForm, amount:e.target.value})}
                className="w-full mt-1.5 bg-app border border-line px-3 py-2 rounded-sm outline-none font-mono" />
            </div>
            <button data-testid="submit-withdraw" className="w-full bg-sell text-white font-bold py-2.5 rounded-sm hover:bg-red-500 uppercase tracking-widest text-xs">{t("submit")}</button>
            {currentFee && currentFee.fee_percent > 0 && (
              <div className="text-[10px] text-dim text-center">Ücret: %{currentFee.fee_percent} + ${currentFee.fee_fixed}</div>
            )}
          </form>

          {/* Transfer */}
          <form onSubmit={submitTransfer} className="bg-panel p-5 space-y-4">
            <div className="flex items-center gap-2"><Send className="w-4 h-4 text-blue-400" /><h2 className="font-semibold uppercase text-xs tracking-widest">Transfer</h2></div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-dim">Alıcı E-posta</label>
              <input required type="email" value={transferForm.recipient_email} onChange={(e)=>setTransfer({...transferForm, recipient_email:e.target.value})}
                placeholder="alici@email.com" className="w-full mt-1.5 bg-app border border-line px-3 py-2 rounded-sm outline-none font-mono" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-dim">Tutar (USD)</label>
              <input type="number" min="1" step="0.01" value={transferForm.amount} onChange={(e)=>setTransfer({...transferForm, amount:e.target.value})}
                className="w-full mt-1.5 bg-app border border-line px-3 py-2 rounded-sm outline-none font-mono" />
            </div>
            <button className="w-full bg-blue-500 text-white font-bold py-2.5 rounded-sm hover:bg-blue-400 uppercase tracking-widest text-xs">Gönder</button>
            <div className="text-[10px] text-dim text-center">Transfer için KYC onayı gereklidir.</div>
          </form>
        </div>

        {/* Transactions */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold tracking-tight">{t("transactions")}</h2>
          <button onClick={downloadTaxReport} className="bg-panel border border-line px-3 py-1.5 rounded-sm text-xs font-bold text-dim hover:text-white flex items-center gap-1.5 hover:border-buy/50 transition-colors">
            <FileDown className="w-3.5 h-3.5" /> Vergi Raporu (CSV)
          </button>
        </div>
        <div className="bg-panel border border-line rounded-sm overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead className="bg-app border-b border-line">
              <tr className="text-dim uppercase tracking-widest text-[10px]">
                <th className="text-left px-4 py-2">Date</th>
                <th className="text-left px-2 py-2">Type</th>
                <th className="text-right px-2 py-2">Amount</th>
                <th className="text-left px-2 py-2">Network</th>
                <th className="text-right px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {txs.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-dim">No transactions</td></tr>}
              {txs.map((r) => (
                <tr key={r.id} data-testid={`tx-${r.id}`} className="border-b border-subtle hover:bg-panel-hover">
                  <td className="px-4 py-2 text-muted">{new Date(r.created_at).toLocaleString()}</td>
                  <td className={`px-2 py-2 uppercase ${r.type === "deposit" ? "text-buy" : "text-sell"}`}>{r.type}</td>
                  <td className="px-2 py-2 text-right">${Number(r.amount).toFixed(2)}</td>
                  <td className="px-2 py-2 text-muted">{r.network}</td>
                  <td className={`px-4 py-2 text-right uppercase ${r.status === "completed" ? "text-buy" : r.status === "pending" ? "text-orange-400" : "text-sell"}`}>{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
